import type { AccountsService } from "@proof/accounts";
export type AccountView = Awaited<ReturnType<AccountsService["view"]>>;
export type AdministrationView = Awaited<
  ReturnType<AccountsService["administration"]>
>;
export class AccountRequestError extends Error {
  constructor(
    readonly code: string,
    readonly status: number,
    readonly deliveryId?: string,
  ) {
    super(
      (accountMessages[code] || code.replaceAll("_", " ").toLowerCase()) +
        (deliveryId
          ? ` Delivery log: ${deliveryId}. See Email delivery logs below for the provider response.`
          : ""),
    );
  }
}
export const accountMessages: Record<string, string> = {
  FEATURE_RELEASE_PREPARING:
    "This feature is being prepared for release. Please try again shortly.",
  BIRD_KEY_REGION_MISMATCH:
    "This API key belongs to a different Bird region. Match the region to the key prefix.",
  BIRD_CONNECTION_CHANGED:
    "This Bird connection changed. Refresh it before saving again.",
  INTEGRATION_CLIENT_STALE:
    "This client changed. Refresh the list before editing it again.",
  INTEGRATION_CLIENT_NOT_FOUND:
    "This integration client is no longer available in this workspace.",
  INTEGRATION_TOKEN_LIMIT:
    "This client has too many active tokens. Reuse an unexpired token or revoke old tokens.",
  INTEGRATION_CONFIGURATION_REQUIRED:
    "Choose the client permissions and token lifetime before saving.",
  SETUP_TOKEN_INVALID:
    "The access token was not accepted. Copy it from the instance's private setup-access.token file.",
  MAGIC_LINK_INVALID:
    "This sign-in link has expired or already been used. Request a new email link.",
  LOGIN_INVALID: "The email or password was not accepted.",
  AUTHENTICATION_REQUIRED: "Sign in again to continue.",
  AUTHENTICATION_RATE_LIMITED: "Too many attempts. Wait before trying again.",
  TWO_FACTOR_CODE_INVALID:
    "That code was not accepted. Check the current code or try another saved recovery code.",
  TWO_FACTOR_REQUIRED: "Complete two-factor verification to continue.",
  TWO_FACTOR_POLICY_CONFLICT:
    "These methods conflict with a parent or child policy. Keep at least one method available at every scope.",
  RECENT_AUTHENTICATION_REQUIRED:
    "Confirm your password below, then retry this change.",
  EMAIL_PROVIDER_NOT_CONFIGURED:
    "Choose Copy setup link to invite without email, or configure and assign an email provider before sending email invitations or codes.",
  EMAIL_DELIVERY_FAILED:
    "Email sending failed. Open Administration → Email providers → Email delivery logs for the provider response.",
  EMAIL_LOG_UNAVAILABLE:
    "Email delivery logs are unavailable. Check application storage before sending another test.",
  EMAIL_LOG_CURSOR_EXPIRED:
    "These logs changed. Refresh delivery logs to start from the latest entries.",
  EMAIL_CONFIGURATION_REQUIRED:
    "Configure a ready default email provider, or explicitly choose to configure email later.",
  GOOGLE_ACCOUNT_NOT_LINKED:
    "This Google account is not linked. Sign in with your password, then connect Google under Account & security.",
  GOOGLE_SENDER_OR_PERMISSION_MISMATCH:
    "Choose the Google account matching the sender address and allow Gmail sending.",
  GOOGLE_OFFLINE_CONSENT_REQUIRED:
    "Google did not issue offline credentials. Reconnect and approve the requested consent.",
  GOOGLE_STATE_EXPIRED:
    "Google authorization expired or was already used. Start the connection again.",
  INVITATION_EXPIRED:
    "This invitation expired, was revoked or was already accepted. Ask your administrator for another invitation.",
  EXISTING_ACCOUNT_PASSWORD_REQUIRED:
    "An account already exists for this email. Enter its current password to accept this invitation.",
  ACCOUNT_INPUT_INVALID:
    "Check the required values. New passwords must contain 12–128 characters.",
  TWO_FACTOR_REQUIRED_BY_POLICY:
    "A site or company requires this second factor. Enroll another allowed method before removing it.",
  PASSWORD_RESET_EXPIRED:
    "This reset link expired or was used. Request another one.",
};
export async function accountRequest<T = Record<string, unknown>>(
  path: string,
  body?: unknown,
  accessToken?: string,
): Promise<T> {
  const response = await fetch("/api/accounts" + path, {
    method: body === undefined ? "GET" : "POST",
    credentials: "same-origin",
    cache: "no-store",
    redirect: "error",
    headers: {
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const value = await response.json().catch(() => ({}));
  if (!response.ok)
    throw new AccountRequestError(
      value.code || value.error || "ACCOUNT_SERVICE_UNAVAILABLE",
      response.status,
      typeof value.deliveryId === "string" &&
        /^[a-f0-9-]{36}$/.test(value.deliveryId)
        ? value.deliveryId
        : undefined,
    );
  return value as T;
}
export async function unlockAccountSetup(token: string) {
  const response = await fetch("/api/accounts/setup/unlock", {
    method: "POST",
    credentials: "same-origin",
    cache: "no-store",
    headers: { Authorization: `Bearer ${token}` },
    redirect: "error",
  });
  const result = await response.json().catch(() => ({}));
  if (!response.ok)
    throw new AccountRequestError(
      result.code || result.error || "SETUP_TOKEN_INVALID",
      response.status,
    );
  return result;
}
export function accountNavigate(path: string) {
  const workspaceLink = location.hash.startsWith("#studio?")
    ? location.hash
    : "";
  history.pushState(null, "", path.includes("#") ? path : path + workspaceLink);
  window.dispatchEvent(new PopStateEvent("popstate"));
}
export async function googleStart(
  purpose: "login" | "link" | "mail",
  mailId?: string,
) {
  const { url } = await accountRequest<{ url: string }>("/google/start", {
    purpose,
    mailId,
  });
  const target = new URL(url);
  if (target.origin !== "https://accounts.google.com")
    throw new Error("Invalid Google authorization address");
  location.assign(target.href);
}
